import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table, TableBody, TableCell, TableContainer,
  TableRow, Paper, Dialog, DialogTitle, DialogContent,
  Typography, Box, Divider, TableHead
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import epochToDate from '../functions/epochdate'; // Adjust path if needed
import InfoTooltip from './InfoTooltip';


const Sector_trends = () => {
  const { columns = [], rows = [] } = useSelector(
    state => state.portfolio?.data?.Sector_trend || {}
  );
  
  const sectorReversalData = useSelector(
    state => state.portfolio?.data?.Sector_Reversal_Data || []
  );

  const sectorTrendPrevious = useSelector(
    state => state.portfolio?.data?.Sector_trend_previous || {}
  );

  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);

  // DAS-2272: derive indices by name from the columns array so the component
  // keeps working under any future column-order change in the backend SELECT.
  const sectorIdx = columns.indexOf('sector');
  const trendIdx = columns.indexOf('trend_direction');
  const epochIdx = columns.indexOf('epoch');
  const explanationIdx = columns.indexOf('explanation');
  const fullReportIdx = columns.indexOf('full_report');

  const getRowColor = (trendDirection) => {
    if (typeof trendDirection !== 'string') return 'transparent';
    
    const trend = trendDirection.toLowerCase();
    if (trend.includes('strong') && trend.includes('bullish')) return '#DFF2BF'; // Light green
    if (trend.includes('moderate') && trend.includes('bullish')) return '#E6F5D0'; // Lighter green
    if (trend.includes('mild') && trend.includes('bullish')) return '#F0F8F0'; // Very light green
    if (trend.includes('neutral')) return '#F5F5F5'; // Light gray
    if (trend.includes('mild') && trend.includes('bearish')) return '#FFF8E1'; // Light yellow
    if (trend.includes('moderate') && trend.includes('bearish')) return '#FFE6B3'; // Light orange
    if (trend.includes('strong') && trend.includes('bearish')) return '#FFB3B3'; // Light red
    if (trend.includes('bearish')) return '#FEEFB3'; // Fallback for generic bearish
    
    return 'transparent';
  };

  const getReversalIndicator = (sectorName) => {
    const sectorData = sectorReversalData.find(item => item.sector === sectorName);
    if (!sectorData || !sectorData.final_reversal_strength) return null;
    
    const strength = sectorData.final_reversal_strength;
    if (strength.includes('NO REVERSAL')) return null;
    
    if (strength.includes('BEARISH')) return '#FF8C00'; // Orange
    if (strength.includes('BULLISH')) return '#90EE90'; // Light green
    
    return '#FF8C00'; // Default to orange for other reversal types
  };

  const getTrendChangeIndicator = (sectorName, currentTrend) => {
    const previousData = sectorTrendPrevious[sectorName];
    if (!previousData) return null;
    
    const previousTrend = previousData.trend_direction;
    if (previousTrend === currentTrend) return null; // No change
    
    // Determine change direction
    const isBullish = (trend) => trend.toLowerCase().includes('bullish');
    const isBearish = (trend) => trend.toLowerCase().includes('bearish');
    
    if (isBullish(currentTrend) && isBearish(previousTrend)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗' }; // Green up arrow
    } else if (isBearish(currentTrend) && isBullish(previousTrend)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘' }; // Red down arrow
    } else if (isBullish(currentTrend) && !isBullish(previousTrend) && !isBearish(previousTrend)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗' }; // Green up arrow
    } else if (isBearish(currentTrend) && !isBearish(previousTrend) && !isBullish(previousTrend)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘' }; // Red down arrow
    }
    
    return { type: 'changed', color: '#FFD700', symbol: '↔' }; // Yellow neutral arrow
  };

  const getTrendComparisonText = (sectorName, currentTrend) => {
    const previousData = sectorTrendPrevious[sectorName];
    if (!previousData) return null;
    
    const previousTrend = previousData.trend_direction;
    if (previousTrend === currentTrend) return null;
    
    return `vs ${previousTrend}`;
  };

  if (!rows || rows.length === 0 || !columns || columns.length === 0) {
    return <Typography sx={{ p: 1, fontSize: '10px' }}>No sector trend data available</Typography>;
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none' }}
      >
        <Table size="small" stickyHeader={false}>
            <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontSize: '14px', fontWeight: 'bold', padding: '0.8px', cursor: 'pointer' }}
                
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Sector Trends
                  <InfoTooltip 
                    text="Market-cap weighted average of 30-day trends from individual stocks, with the analysis based on 300 days of historical data"
                    placement="top"
                    size="small"
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const sectorName = row[sectorIdx];
              const trendDirection = row[trendIdx];
              return (
              <TableRow
                key={idx}
                sx={{
                  backgroundColor: getRowColor(trendDirection),
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f0f0f0' }
                }}
                onClick={() => {
                  setSelectedRow(row);
                  setOpen(true);
                }}
              >
                <TableCell sx={{ fontSize: '9px', padding: '1px', borderBottom: 'none' }}>
                  <strong>{sectorName}</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '9px', padding: '1px', borderBottom: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {trendDirection}
                    {(() => {
                      const indicatorColor = getReversalIndicator(sectorName);
                      return indicatorColor && (
                        <Box
                          sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: indicatorColor,
                            flexShrink: 0
                          }}
                        />
                      );
                    })()}
                    {(() => {
                      const changeIndicator = getTrendChangeIndicator(sectorName, trendDirection);
                      return changeIndicator && (
                        <Box
                          sx={{
                            fontSize: '8px',
                            color: changeIndicator.color,
                            fontWeight: 'bold',
                            ml: 0.5
                          }}
                        >
                          {changeIndicator.symbol}
                        </Box>
                      );
                    })()}
                  </Box>
                  {(() => {
                    const comparisonText = getTrendComparisonText(sectorName, trendDirection);
                    return comparisonText && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '7px', 
                          color: '#666',
                          fontStyle: 'italic'
                        }}
                      >
                        {comparisonText}
                      </Typography>
                    );
                  })()}
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontSize: '16px' }}>
  {selectedRow?.[sectorIdx] || 'Sector'} - Full Report
  {selectedRow?.[epochIdx] && (
    <span style={{ fontWeight: 'normal', fontSize: '13px', marginLeft: '10px', color: '#888' }}>
      ({epochToDate(selectedRow[epochIdx])})
    </span>
  )}
</DialogTitle>

        <DialogContent sx={{ whiteSpace: 'pre-wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Summary:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedRow?.[explanationIdx] || '(no summary)'}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Full Report:
          </Typography>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {selectedRow?.[fullReportIdx] || '(no full report)'}
          </ReactMarkdown>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sector_trends;
